"use client";

export interface BiometricCredentials {
  email: string;
  password: string;
}

/**
 * Serviço responsável pelo gerenciamento de Autenticação Biométrica (Impressão Digital / FaceID)
 * no navegador e no PWA (WebAuthn / Web Crypto API). O app Android nativo tem a sua própria digital.
 */
export class BiometricAuthService {
  private static BIOMETRIC_SETUP_KEY = "biometric_setup";
  private static WEB_BIOMETRIC_CREDENTIAL_KEY = "biometric_enc_cred";

  /**
   * Verifica se a biometria do dispositivo (WebAuthn no navegador ou PWA) está disponível.
   */
  public static async isAvailable(): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    try {
      if (typeof window !== 'undefined' && window.PublicKeyCredential && typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        return available;
      }
    } catch (e) {
      console.log("Erro ao checar suporte a WebAuthn no PWA:", e);
    }

    return false;
  }

  /**
   * Salva as credenciais de forma segura para futuro acesso com biometria.
   */
  public static async setCredentials(credentials: BiometricCredentials): Promise<void> {
    // PWA WebAuthn + Criptografia Web Crypto API
    if (await this.isAvailable()) {
      try {
        const credential = await this.createWebAuthnCredential(credentials.email);
        if (!credential) throw new Error("Validação biométrica não concluída.");

        const encrypted = await this.encryptCredentials(credentials, credential.id);
        localStorage.setItem(this.WEB_BIOMETRIC_CREDENTIAL_KEY, JSON.stringify(encrypted));
        localStorage.setItem(this.BIOMETRIC_SETUP_KEY, 'true');
      } catch (e) {
        console.error("Erro ao configurar biometria PWA:", e);
        throw e;
      }
    }
  }

  /**
   * Solicita o leitor de digital/biometria do dispositivo e retorna as credenciais para login.
   */
  public static async getCredentials(): Promise<BiometricCredentials | null> {
    const stored = localStorage.getItem(this.WEB_BIOMETRIC_CREDENTIAL_KEY);
    if (!stored) {
      // Se não há chave encriptada mas o setup estava marcado, limpa o estado
      return null;
    }

    const credential = await this.verifyWebAuthnCredential();
    if (!credential) return null;

    const encryptedData = JSON.parse(stored);
    return await this.decryptCredentials(encryptedData, credential.id);
  }

  /**
   * Remove as credenciais biométricas salvas.
   */
  public static async deleteCredentials(): Promise<void> {
    localStorage.removeItem(this.BIOMETRIC_SETUP_KEY);
    localStorage.removeItem(this.WEB_BIOMETRIC_CREDENTIAL_KEY);
  }

  // --- MÉTODOS AUXILIARES WEBAUTHN & CRIPTOGRAFIA PARA PWA ---

  private static async createWebAuthnCredential(email: string): Promise<PublicKeyCredential | null> {
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);
    const userId = new TextEncoder().encode(email);

    const publicKeyOptions: PublicKeyCredentialCreationOptions = {
      challenge,
      rp: { name: "AC 3D Studio App" },
      user: {
        id: userId,
        name: email,
        displayName: email,
      },
      pubKeyCredParams: [
        { alg: -7, type: "public-key" },  // ES256
        { alg: -257, type: "public-key" } // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
      },
      timeout: 60000,
    };

    return (await navigator.credentials.create({ publicKey: publicKeyOptions })) as PublicKeyCredential | null;
  }

  private static async verifyWebAuthnCredential(): Promise<PublicKeyCredential | null> {
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge,
      userVerification: "required",
      timeout: 60000,
    };

    return (await navigator.credentials.get({ publicKey: publicKeyOptions })) as PublicKeyCredential | null;
  }

  private static async encryptCredentials(creds: BiometricCredentials, secretKey: string) {
    const enc = new TextEncoder();
    const key = await this.deriveCryptoKey(secretKey);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encryptedContent = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      enc.encode(JSON.stringify(creds))
    );

    return {
      iv: Array.from(iv),
      ciphertext: Array.from(new Uint8Array(encryptedContent)),
    };
  }

  private static async decryptCredentials(encryptedData: { iv: number[]; ciphertext: number[] }, secretKey: string): Promise<BiometricCredentials> {
    const key = await this.deriveCryptoKey(secretKey);
    const iv = new Uint8Array(encryptedData.iv);
    const ciphertext = new Uint8Array(encryptedData.ciphertext);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    const dec = new TextDecoder();
    return JSON.parse(dec.decode(decrypted));
  }

  private static async deriveCryptoKey(secret: string): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const rawKey = enc.encode(secret.padEnd(32, '0').slice(0, 32));
    return await window.crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"]
    );
  }
}
